import React, { useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import {
  CommonDescFive,
  CommonErrorMessage,
  CommonYLine,
} from "@assets/styles/CommonStyles";
import { PrimaryButton } from "@components/buttons";
import { DropdownCalendar } from "@components/calendar";
import { yupBooking } from "@components/inputs/yupValidation";
import { CommonContainer } from "@components/layout/ContainerStyle";
import {
  CheckAvailabilityText,
  CheckInCheckOutText,
  NumberofGuestsText,
  OfferCodeText,
} from "@utils/lanugauge-settings/static-texts/TranslatedTexts";
import { openBookingSite } from "@utils/modify-data/modifyData";
import { IsDesktop } from "@utils/media-query/mediaQuery";
import { useGetFooterQuery } from "@utils/api-requests/GlobalRequests";

import AppTheme, { color } from "src/theme";
import {
  MainBannerBooking,
  MainBannerBookingCheck,
  MainBannerBookingCheckDates,
  MainBannerBookingCode,
  MainBannerBookingGuest,
  MainBannerBookingGuestsButtons,
  MainBannerBookingGuestsContent,
  MainBannerBookingGuestsItem,
  MainBannerBookingHeader,
  MainBannerBookingModalGuestsButton,
} from "./HomeStyling";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { GAClickEvent } from "../../ga/GAConfiguration";

const MainBannerBookingBanner = () => {
  const intl = useIntl();

  const { footerContact } = useGetFooterQuery();

  const calendarRef = useRef();
  const [calendarOpen, setCalendarOpen] = useState(false);

  const {
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(yupBooking),
    mode: "onChange",
    defaultValues: {
      startdate: "",
      adults: 1,
      children: 0,
      enddate: "",
      Noguest: 1,
      offercode: "",
    },
  });

  const handleToggleCalendar = useCallback(() => {
    setCalendarOpen((prev) => {
      if (!prev) {
        setTimeout(() => {
          const calendar = calendarRef.current;
          if (calendar) {
            const calendarRect = calendar.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const bottomOverflow = calendarRect.bottom - viewportHeight;

            if (bottomOverflow > 0) {
              window.scrollBy({
                top: bottomOverflow + 20, // 여유 공간 20px
                behavior: "smooth",
              });
            }
          }
        }, 100); // DOM 업데이트 후 실행되도록 지연
      }
      return !prev;
    });
  }, []);

  const onSubmit = useCallback(
    (data) => {
      GAClickEvent("click_event", {
        action: "checkin",
        category: "widget",
        label: "checkavailability",
      });

      openBookingSite(data, footerContact?.field_offer_default_code);
    },
    [footerContact?.field_offer_default_code],
  );

  const onPlusNumber = useCallback(
    (name) => {
      setValue("Noguest", watch("adults") + watch("children") + 1);
      setValue(name, watch(name) + 1);
    },
    [setValue, watch],
  );

  const onMinusNumber = useCallback(
    (name) => {
      setValue(name, watch(name) - 1);
      setValue("Noguest", watch("adults") + watch("children"));
    },
    [setValue, watch],
  );

  const formatDate = (date) => {
    if (!date) return "yyyy.mm.dd";
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}.${month}.${day}`;
  };

  return (
    <IsDesktop>
      <CommonContainer>
        <MainBannerBooking onSubmit={handleSubmit(onSubmit)}>
          {/* Area 1 */}
          <MainBannerBookingCheck
            onClick={handleToggleCalendar}
            data-exclude="true"
          >
            <MainBannerBookingHeader>
              <CheckInCheckOutText />

              <CommonErrorMessage>
                {errors.startdate?.message}
              </CommonErrorMessage>
            </MainBannerBookingHeader>

            <MainBannerBookingCheckDates>
              <CommonDescFive>{formatDate(watch("startdate"))}</CommonDescFive>

              <span>{"──>"}</span>

              <CommonDescFive>{formatDate(watch("enddate"))}</CommonDescFive>
            </MainBannerBookingCheckDates>

            <DropdownCalendar
              calendarRef={calendarRef}
              calendarOpen={calendarOpen}
              setCalendarOpen={setCalendarOpen}
              setValue={setValue}
            />
          </MainBannerBookingCheck>

          <CommonYLine height="60%" />

          {/* Area 2 */}
          <MainBannerBookingGuest>
            <MainBannerBookingHeader>
              <NumberofGuestsText />
            </MainBannerBookingHeader>

            <MainBannerBookingGuestsContent>
              {["Adults", "Children"].map((people, idx) => {
                const name = people.toLowerCase();
                return (
                  <MainBannerBookingGuestsItem key={idx}>
                    <CommonDescFive>{people}</CommonDescFive>

                    <CommonYLine height="1.2rem" />

                    <MainBannerBookingGuestsButtons>
                      <MainBannerBookingModalGuestsButton
                        type="button"
                        inActive={watch(name) < (name === "children" ? 1 : 2)}
                        onClick={() => onMinusNumber(name)}
                      >
                        <FontAwesomeIcon icon={faMinus} />
                      </MainBannerBookingModalGuestsButton>

                      <CommonDescFive>{watch(name)}</CommonDescFive>

                      <MainBannerBookingModalGuestsButton
                        type="button"
                        inActive={watch("Noguest") > 3}
                        onClick={() => onPlusNumber(name)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </MainBannerBookingModalGuestsButton>
                    </MainBannerBookingGuestsButtons>
                  </MainBannerBookingGuestsItem>
                );
              })}
            </MainBannerBookingGuestsContent>
          </MainBannerBookingGuest>

          <CommonYLine height="60%" />

          {/* Area 3 */}
          <MainBannerBookingCode>
            <MainBannerBookingHeader>
              <OfferCodeText />
            </MainBannerBookingHeader>

            <input
              type="text"
              placeholder={intl.formatMessage({
                id: "lang-enter-code-here",
              })}
              {...register("offercode")}
            />
          </MainBannerBookingCode>

          {/* Check Availability */}
          <PrimaryButton
            noArrow
            buttonType="submit"
            customstyle={{
              width: "fit-content",
              borderRadius: 0,
              fontWeight: AppTheme.fontWeight.medium,
              backgroundColor: color.yellow_secondary,
              flex: 1,
            }}
          >
            <CheckAvailabilityText />
          </PrimaryButton>
        </MainBannerBooking>
      </CommonContainer>
    </IsDesktop>
  );
};

export default MainBannerBookingBanner;
